import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftIcon, StarIcon } from 'lucide-react'
import { EmptyState } from '@/components/empty-state'
import { Favicon } from '@/components/favicon'
import { Panel } from '@/components/panel'
import { SerpStar } from '@/components/serp-star'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { bareUrl, formatCount, formatDate } from '@/lib/format'
import {
  beyondLabel,
  featureLabel,
  featureLines,
  marketLabel,
  type SerpDetail,
  type SerpResult,
  useKeywordSerp,
} from '@/lib/serp'
import { cn } from '@/lib/utils'
import { BACK_LINK, DetailHeader, useSiteScope } from '../-section'

export const Route = createFileRoute('/_app/$org/$host/search/serp/$queryId')({
  component: KeywordSerpPage,
})

/**
 * One results page, as it was when we read it.
 *
 * The ten links are the subject here, not a metric about them, so they are the
 * page: what ranks, in the order Google put it, with this site's own rows
 * marked. Plum marks them for the reason plum marks a selected row — of the
 * ten, these are the ones the reader came for.
 */
function KeywordSerpPage() {
  const { org, host, range, site } = useSiteScope()
  const { queryId } = Route.useParams()
  const { data, isPending, error } = useKeywordSerp(site, Number(queryId))

  const back = (
    <Link
      to="/$org/$host/search/serp"
      params={{ org, host }}
      search={{ range }}
      className={BACK_LINK}
    >
      <ArrowLeftIcon className="size-3" aria-hidden />
      SERP
    </Link>
  )

  if (error) {
    return (
      <>
        <DetailHeader back={back} title="Keyword not found" />
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            title="Keyword not found"
            description={`Nothing in ${host} has been recorded under that keyword. It may belong to another site.`}
            action={
              <Button asChild size="sm" variant="outline">
                <Link to="/$org/$host/search/serp" params={{ org, host }} search={{ range }}>
                  Back to SERP
                </Link>
              </Button>
            }
          />
        </div>
      </>
    )
  }

  if (isPending || !data) {
    return (
      <>
        <DetailHeader back={back} title="…" quoted />
        <div className="space-y-4">
          <Skeleton className="h-20 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </>
    )
  }

  return (
    <>
      <DetailHeader
        back={back}
        title={data.keyword.text}
        quoted
        actions={
          <SerpStar
            site={site}
            host={host}
            queryId={data.keyword.id}
            keyword={data.keyword.text}
            tracked={data.tracked !== null}
          />
        }
      />

      {data.check === null ? (
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            title="Not read yet"
            description={
              data.tracked
                ? `The results page for this keyword has not been read yet. It appears here as soon as it has been.`
                : `Nobody has read the results page for this keyword. Star it and it will be.`
            }
          />
        </div>
      ) : (
        <div className="space-y-4">
          <Standing data={data} host={host} />
          <Results data={data} host={host} />
          <div className="grid gap-4 lg:grid-cols-2">
            <Readings data={data} />
            <Features data={data} />
          </div>
        </div>
      )}
    </>
  )
}

/** Where this site sits, said once and with the depth that qualifies it. */
function Standing({ data, host }: { data: SerpDetail; host: string }) {
  const best = data.series.at(-1)
  const position = best?.position ?? null
  const check = data.check

  return (
    <section className="flex flex-wrap items-baseline gap-x-6 gap-y-2 rounded-lg border border-border bg-card px-4 py-3.5">
      <span className="mono-label text-muted-foreground">{host}</span>

      {position === null ? (
        <span className="font-mono text-[18px] leading-none tabular">
          {beyondLabel(check?.pages ?? 1)}
        </span>
      ) : (
        <span className="font-mono text-[18px] leading-none text-plum tabular">#{position}</span>
      )}

      <span className="ml-auto flex items-center gap-4 font-mono text-[11px] text-muted-foreground tabular">
        <span>{marketLabel(data.market)}</span>
        <span>{check ? formatDate(check.checkDate) : '—'}</span>
        <span>{check?.resultCount ?? 0} results</span>
      </span>
    </section>
  )
}

/**
 * The page as it was printed, in this interface's voice rather than Google's.
 *
 * The reading order is the one every reader already has — who it is, what it is
 * called, what it says — so what outranks this site is taken in at a glance.
 * What is not borrowed is the scale: hierarchy here is the title being the only
 * ink on a row of grey, not the title being large. Three lines is the whole of
 * a result; the decorations are a footnote under them in the register they are
 * compared in, so a result that carries none is the same shape as one that
 * carries all three.
 */
function Results({ data, host }: { data: SerpDetail; host: string }) {
  return (
    <Panel title="Results" meta={String(data.results.length)} flush>
      <ol>
        {data.results.map((result) => (
          <ResultRow key={`${result.page}-${result.position}`} result={result} host={host} />
        ))}
      </ol>
    </Panel>
  )
}

function ResultRow({ result, host }: { result: SerpResult; host: string }) {
  const ours = result.host === host || (result.host?.endsWith(`.${host}`) ?? false)
  // Titles rather than a count: what a brand's own sitelinks say is the fold it
  // owns above whatever ranks second.
  const sitelinks = (result.sitelinks ?? [])
    .map((link) => link.title)
    .filter((title): title is string => Boolean(title))

  return (
    <li
      className={cn(
        'group flex gap-3 border-b border-border px-4 py-3.5 last:border-b-0',
        ours ? 'bg-plum-selected' : 'hover:bg-secondary/30',
      )}
    >
      <span
        className={cn(
          'w-6 shrink-0 pt-1.5 text-right font-mono text-[11px] tabular',
          ours ? 'text-plum' : 'text-muted-foreground/60',
        )}
      >
        {result.position}
      </span>

      <div className="min-w-0 flex-1">
        {/* The favicon sits in a ring rather than loose against the crumbs: at
            16px beside 11px type it is the largest thing on the line either
            way, and a hairline circle makes that deliberate. */}
        <div className="flex items-center gap-2.5">
          {result.host && (
            <span className="grid size-6 shrink-0 place-items-center rounded-full border border-border bg-background">
              <Favicon host={result.host} size={13} />
            </span>
          )}
          <span
            className="truncate font-mono text-[11.5px] text-muted-foreground"
            title={result.url}
          >
            {bareUrl(result.url)}
          </span>
        </div>

        <a
          href={result.url}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-1.5 block truncate text-[13.5px] leading-snug underline-offset-2 hover:text-plum hover:underline"
        >
          {result.title ?? result.url}
        </a>

        {result.snippet && (
          <p className="mt-1 line-clamp-2 text-[12.5px] leading-relaxed text-muted-foreground">
            {result.snippet}
          </p>
        )}

        {(result.rating || result.shownDate || sitelinks.length > 0) && (
          <div className="mt-1.5 flex min-w-0 items-center gap-3 text-[11px] text-muted-foreground/60">
            {result.rating && (
              <span className="inline-flex shrink-0 items-center gap-1 font-mono tabular">
                <StarIcon className="size-3 fill-current" aria-hidden />
                {result.rating}
                {result.ratingCount !== null && ` · ${formatCount(result.ratingCount)}`}
              </span>
            )}
            {result.shownDate && (
              <span className="shrink-0 font-mono tabular">{result.shownDate}</span>
            )}
            {sitelinks.length > 0 && <span className="truncate">{sitelinks.join(' · ')}</span>}
          </div>
        )}
      </div>
    </li>
  )
}

/** Every reading we hold, newest first. The series rather than a chart: these
 * arrive when they arrive, so the dates are the axis and there are usually few
 * enough of them to read. */
function Readings({ data }: { data: SerpDetail }) {
  return (
    <Panel title="Readings" meta={String(data.series.length)} flush>
      <ul>
        {[...data.series].reverse().map((reading) => (
          <li
            key={reading.checkDate}
            className="flex items-baseline gap-3 border-b border-border px-4 py-2 text-[12.5px] last:border-b-0"
          >
            <span className="font-mono text-[11px] text-muted-foreground tabular">
              {formatDate(reading.checkDate)}
            </span>
            <span className="ml-auto font-mono tabular">
              {reading.position === null ? beyondLabel(reading.pages) : `#${reading.position}`}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  )
}

/**
 * What else was on the page, and what it said.
 *
 * The answer to clicks falling while the position did not move, which is the
 * question this feed exists to settle — and half of that answer is the content:
 * four questions in a "people also ask" that the site's page answers better is
 * a different finding from the block merely being present. Blocks are stored
 * verbatim, so they are read verbatim: whatever reduces to lines is shown as
 * lines, and whatever does not is its heading, which is still the finding.
 */
function Features({ data }: { data: SerpDetail }) {
  return (
    <Panel title="On the page" meta={String(data.features.length)} flush>
      {data.features.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-muted-foreground">Ten links and nothing else.</p>
      ) : (
        <ul>
          {data.features.map((feature) => {
            const lines = featureLines(feature.payload)
            return (
              <li
                key={`${feature.page}-${feature.kind}`}
                className="border-b border-border px-4 py-3 last:border-b-0"
              >
                <p className="mono-label text-muted-foreground">{featureLabel(feature.kind)}</p>
                {lines.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {lines.map((line) => (
                      <li key={line} className="truncate text-[12.5px]" title={line}>
                        {line}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Panel>
  )
}
